import {
  CoverageAccordionSection,
  DefaultInputType,
  FormGeneratorProps,
} from "@platform/design-system";

import { ClientProductCarrierWithCarrier } from "../../../../types/rfpApi";

export type InputType =
  | DefaultInputType
  | "incumbentData"
  | "contractedCarrier"
  | "lastPayment";
export type ProductCoverageMap = Map<number, ClientProductCarrierWithCarrier>;

export type CoverageFormValues = {
  rfpProductId?: number;
  incumbentProductId?: number;
  incumbentCarrierId?: number;
  incumbentData?: Record<string, unknown>;
  incumbentAnnualPremium?: string;
  incumbentContractedCarrier?: boolean;
  incumbentGaOverridePercentage?: string;
  incumbentGaAnnualOverrideAmount?: string;
  finalDecisionSelected?: SelectableFinalDecision;
  winningCarrierId?: number;
  renewalDate?: Date;
  annualWrittenPremium?: string;
  numberOfPlansQuoted?: string;
  sold?: boolean;
  rateGuarantee?: string;
  techCredits?: boolean;
  winningContractedCarrier?: boolean;
  winningMedicalCarrier?: boolean;
  winningGaOverridePercentage?: string;
  winningGaAnnualOverrideAmount?: string;
  notes?: string;
  incumbentMedicalCarrier?: boolean;
  lastEffectiveDate?: string;
  lastRateGuarantee?: string;
  renewalDateManualEntry?: boolean;
  incumbentProductIdManualEntry?: number | null;
};

export type FinalDecisionFormValues = {
  wonBOR?: string;
  lostBrokerOfRecord?: boolean;
  currentCoverages?: CoverageFormValues[];
  newCoverages?: CoverageFormValues[];
};

export enum FinalDecisionStatus {
  SoldNewLine = "soldNewLine",
  MovingCarriers = "movingCarriers",
  RenewingWithIncumbent = "renewingWithIncumbent",
  BORNotWon = "borNotWon",
  LostBOR = "lostBor",
  DidNotSell = "didNotSell",
  TerminatingCoverage = "terminatingCoverage",
}
// These FD statuses are selectable in the form
export const SELECTABLE_FINAL_DECISIONS = [
  FinalDecisionStatus.MovingCarriers,
  FinalDecisionStatus.RenewingWithIncumbent,
  FinalDecisionStatus.TerminatingCoverage,
] as const;
type SelectableFinalDecision = (typeof SELECTABLE_FINAL_DECISIONS)[number];

export const isSelectableFinalDecision = (
  value: string,
): value is SelectableFinalDecision =>
  SELECTABLE_FINAL_DECISIONS.includes(value as SelectableFinalDecision);

// A FinalDecision's CoverageDecision record from the API
export interface CoverageDecisionRecord {
  id: number;
  createdAt: string;
  updatedAt: string;
  finalDecisionStatus: FinalDecisionStatus | null;
  rfpProductId: number | null;
  incumbentProductId: number | null;
  winningCarrierId: number | null;
  isCurrentCoverage: boolean;
  incumbentAnnualPremium: number | null;
  winningAnnualPremium: number | null;
  numberOfPlansQuoted: number | null;
  renewalDate: string | null;
  rateGuarantee: number | null;
  techCredits: boolean | null;
  notes: string | null;
  incumbentGaOverridePercentage?: number | null;
  incumbentGaAnnualOverrideAmount?: number | null;
  winningGaOverridePercentage?: number | null;
  winningGaAnnualOverrideAmount?: number | null;
  incumbentCarrierGaContractStatus?: boolean | null;
  winningCarrierGaContractStatus?: boolean | null;
  incumbentCarrierGaIsMedical?: boolean | null;
  winningCarrierGaIsMedical?: boolean | null;
  winningCarrierTier?: string | null;
  incumbentCarrierTier?: string | null;
}

export interface CoverageDecisionRequestPayload {
  finalDecisionStatus: FinalDecisionStatus | null;
  rfpProductId: number | null;
  incumbentProductId?: number | null;
  incumbentCarrierId?: number | null;
  winningCarrierId?: number | null;
  isCurrentCoverage?: boolean;
  incumbentAnnualPremium?: number | null;
  incumbentCarrierGaContractStatus?: boolean | null;
  winningAnnualPremium?: number | null;
  winningCarrierGaContractStatus?: boolean | null;
  renewalDate?: string | null;
  numberOfPlansQuoted?: number | null;
  incumbentGaOverridePercentage?: number | null;
  incumbentGaAnnualOverrideAmount?: number | null;
  rateGuarantee?: number | null;
  winningGaOverridePercentage?: number | null;
  winningGaAnnualOverrideAmount?: number | null;
  techCredits?: boolean | null;
  notes?: string | null;
}

export interface RfpFinalDecisionResponse {
  rfpId: number;
  finalDecisionId: number;
  draftId: number | null;
  formData: FinalDecisionFormValues;
  coverageDecisions: CoverageDecisionRecord[];
  createdAt: string;
  currentStep: number;
  lastDraftSavedAt: string;
}

export interface UnsoldDecisionCoverage {
  product: { id: number; name: string };
  isCurrent: boolean | null;
  decision: FinalDecisionStatus | null;
}

// The response from submitting coverage decisions
export interface SubmitCoverageDecisionsResponse {
  currentCoverages: CoverageDecisionRecord[];
  newCoverages: CoverageDecisionRecord[];
}

// The FinalDecision response with transformed form values added
export interface FinalDecisionsWithSubmittedForm extends RfpFinalDecisionResponse {
  submittedFormValues: FinalDecisionFormValues | null;
}

export interface ReviewSection {
  title: string;
  sections: CoverageAccordionSection[];
  chip?: {
    label: string;
    tooltip?: string;
  };
}

export type FinalDecisionFormGeneratorProps = FormGeneratorProps<
  InputType,
  FinalDecisionFormValues
>;

export interface ReviewDataResponseCoverage {
  name: string;
  winningCarrier: string | null;
  winningContractedCarrier: string | null;
  annualWrittenPremium: string | null;
  renewalDate: string | null;
  rateGuarantee: string | null;
  winningGaOverridePercentage: string | null;
  winningGaAnnualOverrideAmount: string | null;
  techCredits: string | null;
  numberOfPlansQuoted: string | null;
  notes: string | null;
  finalDecision: string | null;
  isMissing?: {
    finalDecisionSelected?: boolean;
    winningCarrier?: boolean;
    winningContractedCarrier?: boolean;
    annualWrittenPremium?: boolean;
    renewalDate?: boolean;
    rateGuarantee?: boolean;
    winningGaOverridePercentage?: boolean;
    winningGaAnnualOverrideAmount?: boolean;
    techCredits?: boolean;
    numberOfPlansQuoted?: boolean;
  };
}

export interface ReviewDataResponseCurrentCoverage extends ReviewDataResponseCoverage {
  incumbentProduct: string | null;
  incumbentContractedCarrier: string | null;
  incumbentAnnualPremium: string | null;
  incumbentGaOverridePercentage: string | null;
  incumbentGaAnnualOverrideAmount: string | null;
  isMissing?: {
    finalDecisionSelected?: boolean;
    incumbentProductId?: boolean;
    incumbentAnnualPremium?: boolean;
    incumbentGaOverridePercentage?: boolean;
    incumbentGaAnnualOverrideAmount?: boolean;
    winningCarrier?: boolean;
    annualWrittenPremium?: boolean;
    renewalDate?: boolean;
    rateGuarantee?: boolean;
    winningGaOverridePercentage?: boolean;
    winningGaAnnualOverrideAmount?: boolean;
    techCredits?: boolean;
    numberOfPlansQuoted?: boolean;
  };
}

export interface ReviewDataResponse {
  currentCoverages: ReviewDataResponseCurrentCoverage[];
  newCoverages: ReviewDataResponseCoverage[];
}
